import React from "react";
import { motion } from "framer-motion";
import { Armchair, Heart } from "lucide-react";

// Threshold 1 — first occupancy registered. Under this, a chair is still "Waiting".
const STAGE1_MIN = 6;

const STYLES = {
  empty: { bg: "bg-[#232C40]", ring: "border-[#334063]", dot: "#52525B", label: "Empty" },
  waiting: { bg: "bg-sky-500/15", ring: "border-sky-500/60", dot: "#38BDF8", label: "Waiting" },
  awaiting: { bg: "bg-amber-500/15", ring: "border-amber-500/60", dot: "#F59E0B", label: "Initial engagement" },
  matched: { bg: "bg-emerald-500/15", ring: "border-emerald-500/60", dot: "#10B981", label: "Matched" },
  violation: { bg: "bg-red-500/20", ring: "border-red-500/70", dot: "#EF4444", label: "No sale" },
  tampered: { bg: "bg-fuchsia-500/15", ring: "border-fuchsia-500/70", dot: "#a21caf", label: "Tampered" },
  offline: { bg: "bg-[#171E2E]", ring: "border-[#2A3550] border-dashed", dot: "#3F3F46", label: "Offline" },
};

export function StatusDot({ color, offline = false, pulse = false, className = "" }) {
  return (
    <span
      className={`relative inline-block h-2.5 w-2.5 rounded-full ${pulse ? "pulse-status" : ""} ${className}`}
      style={{ backgroundColor: color }}
    >
      {offline && (
        <span className="absolute left-1/2 top-1/2 h-[2px] w-[150%] -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-full bg-red-500" />
      )}
    </span>
  );
}

export default function ChairNode({ chair, index = 0, onClick, stage1Min = STAGE1_MIN }) {
  const isOffline = chair.active === false || chair.status === "offline";
  let status = isOffline ? "offline" : chair.status;
  if (!isOffline && chair.occupied && chair.status === "awaiting" && (chair.minutes_seated ?? 0) < stage1Min) {
    status = "waiting"; // occupied but threshold 1 not yet passed
  }
  const s = STYLES[status] || STYLES.empty;
  const pulse = !isOffline && (status === "waiting" || status === "awaiting" || status === "violation" || status === "tampered");
  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, delay: index * 0.03 }}
      onClick={onClick}
      data-testid={`chair-node-${chair.id}`}
      className={`relative aspect-square rounded-xl border ${s.ring} ${s.bg} flex flex-col items-center justify-center gap-1 hover:-translate-y-0.5 transition-transform duration-200 ${isOffline ? "opacity-60" : ""}`}
    >
      <span className="absolute top-2 right-2">
        <StatusDot color={s.dot} offline={status === "offline"} pulse={pulse} />
      </span>
      {chair.charitable && !isOffline && (
        <span
          className="absolute top-1.5 left-1.5 h-4 w-4 rounded-full bg-rose-500/20 border border-rose-400/50 flex items-center justify-center"
          title={`Charitable chair${chair.beneficiary_name ? ` · ${chair.beneficiary_name}` : ""}`}
          data-testid={`chair-charitable-badge-${chair.id}`}
        >
          <Heart className="h-2.5 w-2.5 text-rose-300" />
        </span>
      )}
      {chair.role === "ancillary" && !isOffline && (
        <span
          className="absolute bottom-1.5 left-1.5 rounded-full bg-slate-500/20 border border-slate-400/40 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-wide text-slate-300"
          title="Ancillary station — wash/processing, never expects a sale"
          data-testid={`chair-ancillary-badge-${chair.id}`}
        >
          Anc
        </span>
      )}
      <Armchair className="h-6 w-6 text-slate-300" />
      <span className="text-[11px] font-medium text-slate-100">{chair.label}</span>
      <span className="text-[10px] text-slate-400">
        {isOffline ? "Offline" : chair.occupied ? `${chair.minutes_seated} min` : "—"}
      </span>
    </motion.button>
  );
}

export function FloorLegend() {
  return (
    <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400">
      {Object.entries(STYLES).map(([k, v]) => (
        <div key={k} className="flex items-center gap-2">
          <StatusDot color={v.dot} offline={k === "offline"} />
          {v.label}
        </div>
      ))}
    </div>
  );
}
